'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import type { EmblaOptionsType } from 'embla-carousel-react';
import type { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { SectionProps } from './custom-homepage';

import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import {
  CarouselProvider,
  useCarouselContext,
} from '@eliadmin/core/src/components/ui/carousel-context';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';

type ServicesProps = { items: Service[] } & Partial<SectionProps>;

const OPTIONS: EmblaOptionsType = {
  align: 'start',
  containScroll: false,
};

export function Services(_props: ServicesProps) {
  return (
    <section className={cn('custom-section', 'bg-secondary py-16 pr-0 sm:py-20 lg:py-24')}>
      <CarouselProvider>
        <ServicesDesktop _props={_props} />
      </CarouselProvider>
      <CarouselProvider>
        <ServicesMobile _props={_props} />
      </CarouselProvider>
    </section>
  );
}

function ServicesDesktop({ _props }: { _props: ServicesProps }) {
  const { selectedIndex } = useCarouselContext()!;

  return (
    <div
      style={{ clipPath: 'inset(-100vw -100vw -100vw 0)' }}
      className={cn('custom-container--xxl', 'relative hidden overflow-visible xl:flex')}
    >
      {_props.title && _props.subtitle && (
        <Heading
          title={_props.title}
          subtitle={_props.subtitle}
          className={{
            container: 'absolute left-0 ml-[541px] w-full max-w-[1059px]',
            title: 'w-full max-w-[628px] text-primary',
          }}
        />
      )}
      <Carousel
        options={OPTIONS}
        className={{
          slide: 'h-fit w-full max-w-[517px]',
          container: 'h-[659px] w-full items-end gap-4 sm:gap-5 lg:gap-6',
          wrapper: 'h-[659px] w-full',
          main: 'h-[659px] w-full',
        }}
        slides={_props.items.map((service, index) => (
          <ServiceCard service={service} key={service.slug} isActive={index === selectedIndex} />
        ))}
      />
      <CarouselControls
        className={{
          main: 'absolute bottom-0 left-0 ml-[541px] flex w-full max-w-[1059px] items-center justify-start gap-2',
          button:
            'z-10 h-14 min-w-[88px] max-w-[88px] rounded-full bg-white p-0 transition-all hover:bg-white/80',
          next: 'text-black',
          prev: 'text-black',
        }}
      />
    </div>
  );
}

function ServicesMobile({ _props }: { _props: ServicesProps }) {
  return (
    <div
      style={{ clipPath: 'inset(-100vw -100vw -100vw 0)' }}
      className={cn(
        'custom-container--xxl',
        'flex flex-col items-start justify-start gap-8 sm:gap-10 xl:hidden',
      )}
    >
      {_props.title && _props.subtitle && (
        <Heading
          title={_props.title}
          subtitle={_props.subtitle}
          className={{ container: 'pr-4', title: 'text-primary' }}
        />
      )}
      <Carousel
        options={OPTIONS}
        className={{
          slide: 'h-fit w-full max-w-[330px] sm:max-w-[400px] md:max-w-[517px]',
          container: 'h-[421px] w-full items-end gap-4 sm:h-[500px] sm:gap-5 lg:gap-6',
          wrapper: 'h-[421px] w-full sm:h-[500px]',
          main: 'h-[421px] w-full sm:h-[500px]',
        }}
        slides={_props.items.map(service => (
          <ServiceCard service={service} key={service.slug} isActive={true} />
        ))}
      />
      <CarouselControls
        className={{
          main: 'flex w-full max-w-[1059px] items-center justify-start gap-2',
          button:
            'z-10 h-14 min-w-[88px] max-w-[88px] rounded-full bg-white p-0 transition-all hover:bg-white/80',
          next: 'text-black',
          prev: 'text-black',
        }}
      />
    </div>
  );
}

function ServiceCard({ service, isActive }: { service: Service; isActive: boolean }) {
  const isDesktop = useBreakpoint('sm');
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'relative flex w-full items-end justify-end overflow-hidden rounded-[20px] p-4 transition-all duration-500 ease-in-out md:p-6 lg:p-8',
        isActive ? 'h-[421px] sm:h-[500px] xl:h-[659px]' : 'xl:mb-[104px] xl:h-[350px]',
      )}
    >
      <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
        <Heading
          title={service.name}
          className={{ title: 'text-white' }}
          tag={isActive ? 'h2' : 'h3'}
        />
        {/* <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4 overflow-hidden transition-all duration-500 ease-in-out sm:gap-5 lg:gap-6',
            isActive
              ? 'max-h-[300px] translate-y-0 opacity-100'
              : 'max-h-0 translate-y-2 opacity-0',
          )}
        >
          <Paragraph
            innerHtml={service?.short_description}
            color="white"
            size={isDesktop ? 'lg' : 'base'}
          />
          <Button variant={'secondary'} className="w-fit">
            {t('text792')}
          </Button>
        </div> */}
      </div>
      <div className="main-overlay" />
      <div className="main-overlay-overlay" />
      <Image
        src={service?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(service?.image, service?.name)}
        fill
        className="absolute inset-0 z-0 h-full w-full object-cover object-center grayscale"
      />
    </div>
  );
}
